import { useEffect, useState, type ReactNode } from "react";

import { api, setAccessToken } from "../../lib/api";
import type { PlanCode, User } from "../../types/api";
import { AuthContext } from "./authContext";

interface MeResponse {
  user_id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_plan: PlanCode;
  email: string;
  role: User["role"];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenantPlan, setTenantPlan] = useState<PlanCode | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hydrate session on mount.
    (async () => {
      try {
        const resp = await api.get<MeResponse>("/api/v1/auth/me");
        setUser({
          id: resp.data.user_id,
          tenant_id: resp.data.tenant_id,
          email: resp.data.email,
          full_name: null,
          role: resp.data.role,
          is_active: true,
        });
        setTenantPlan(resp.data.tenant_plan);
      } catch {
        setUser(null);
        setTenantPlan(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signIn = async (email: string, password: string) => {
    const resp = await api.post<{ access_token: string }>("/api/v1/auth/login", { email, password });
    setAccessToken(resp.data.access_token);
    // Re-hydrate
    const me = await api.get<MeResponse>("/api/v1/auth/me");
    setUser({
      id: me.data.user_id,
      tenant_id: me.data.tenant_id,
      email: me.data.email,
      full_name: null,
      role: me.data.role,
      is_active: true,
    });
    setTenantPlan(me.data.tenant_plan);
  };

  const signOut = () => {
    setAccessToken(null);
    setUser(null);
    setTenantPlan(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, tenantPlan, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
